import {
  RestaurantReportCode,
  RestaurantReportFilterConfig,
  RestaurantReportTypeDTO,
} from '../../../types/restaurant/RestaurantReportTypes';

export const fallbackRestaurantReports: RestaurantReportTypeDTO[] = [
  {
    id: 1,
    code: 'RESTAURANT_SALES_SUMMARY',
    name: 'Resumen de ventas',
    description:
      'Ingresos, tickets, descuentos, impuestos y promedio por orden.',
    category: 'Ventas',
    recommendedFormat: 'XLSX',
    displayOrder: 1,
  },
  {
    id: 2,
    code: 'RESTAURANT_ORDERS_DETAIL',
    name: 'Detalle de ordenes',
    description: 'Ordenes por mesa, salon, estado, mesero y tipo de servicio.',
    category: 'Operacion',
    recommendedFormat: 'XLSX',
    displayOrder: 2,
  },
  {
    id: 3,
    code: 'RESTAURANT_MENU_ITEM_SALES',
    name: 'Platillos mas vendidos',
    description:
      'Ranking de productos del menu, cantidades, margen y venta neta.',
    category: 'Menu',
    recommendedFormat: 'XLSX',
    displayOrder: 3,
  },
  {
    id: 4,
    code: 'RESTAURANT_RAW_MATERIAL_USAGE',
    name: 'Consumo de materia prima',
    description: 'Materia prima consumida por recetas, ordenes y produccion.',
    category: 'Inventario',
    recommendedFormat: 'XLSX',
    displayOrder: 4,
  },
  {
    id: 5,
    code: 'RESTAURANT_RAW_MATERIAL_STOCK',
    name: 'Existencias de materia prima',
    description: 'Stock actual, minimos, maximos, costos y alertas.',
    category: 'Inventario',
    recommendedFormat: 'XLSX',
    displayOrder: 5,
  },
  {
    id: 6,
    code: 'RESTAURANT_INVENTORY_MOVEMENTS',
    name: 'Movimientos de inventario',
    description: 'Entradas, salidas, ajustes y trazabilidad de materia prima.',
    category: 'Inventario',
    recommendedFormat: 'XLSX',
    displayOrder: 6,
  },
  {
    id: 7,
    code: 'RESTAURANT_PURCHASES',
    name: 'Compras de materia prima',
    description:
      'Ordenes de compra por proveedor, estado, fecha y materia prima.',
    category: 'Compras',
    recommendedFormat: 'XLSX',
    displayOrder: 7,
  },
  {
    id: 8,
    code: 'RESTAURANT_RECIPE_COSTS',
    name: 'Costo de recetas',
    description: 'Costo estimado, precio de venta y margen por platillo.',
    category: 'Costos',
    recommendedFormat: 'XLSX',
    displayOrder: 8,
  },
  {
    id: 9,
    code: 'RESTAURANT_KITCHEN_PERFORMANCE',
    name: 'Rendimiento de cocina',
    description: 'Tiempos de preparacion por estacion, orden e item.',
    category: 'Cocina',
    recommendedFormat: 'XLSX',
    displayOrder: 9,
  },
  {
    id: 10,
    code: 'RESTAURANT_RESERVATIONS',
    name: 'Reservaciones',
    description: 'Reservas por fecha, estado, salon y cliente.',
    category: 'Reservas',
    recommendedFormat: 'XLSX',
    displayOrder: 10,
  },
  {
    id: 11,
    code: 'RESTAURANT_CORPORATE_CONSUMPTIONS',
    name: 'Consumos corporativos',
    description: 'Consumos por cuenta, estado, fecha y platillo del menú.',
    category: 'Operacion',
    recommendedFormat: 'XLSX',
    displayOrder: 11,
  },
  {
    id: -12,
    code: 'RESTAURANT_WAITER_TIPS',
    name: 'Propinas por mesero',
    description:
      'Ventas, líneas de comida, platos, propinas y porcentaje promedio por mesero.',
    category: 'Ventas',
    recommendedFormat: 'XLSX',
    displayOrder: 12,
  },
  {
    id: -13,
    code: 'RESTAURANT_WAITER_TIPS_DETAIL',
    name: 'Detalle de propinas por mesero',
    description: 'Cada venta con propina de un mesero, con totales al final.',
    category: 'Ventas',
    recommendedFormat: 'XLSX',
    displayOrder: 13,
  },
];

const dateRange: RestaurantReportFilterConfig[] = [
  { name: 'dateFrom', label: 'Desde', type: 'date', required: true },
  { name: 'dateTo', label: 'Hasta', type: 'date', required: true },
];

export const restaurantReportFiltersMap: Partial<
  Record<RestaurantReportCode, RestaurantReportFilterConfig[]>
> = {
  RESTAURANT_SALES_SUMMARY: [
    ...dateRange,
    {
      name: 'serviceType',
      label: 'Tipo de servicio',
      type: 'select',
      optionSource: 'serviceTypes',
    },
    {
      name: 'diningRoomId',
      label: 'Salon',
      type: 'select',
      optionSource: 'diningRooms',
    },
  ],
  RESTAURANT_ORDERS_DETAIL: [
    ...dateRange,
    {
      name: 'status',
      label: 'Estado',
      type: 'select',
      optionSource: 'orderStatuses',
    },
    {
      name: 'serviceType',
      label: 'Tipo de servicio',
      type: 'select',
      optionSource: 'serviceTypes',
    },
    { name: 'tableId', label: 'Mesa', type: 'select', optionSource: 'tables' },
  ],
  RESTAURANT_MENU_ITEM_SALES: [
    ...dateRange,
    {
      name: 'menuCategoryId',
      label: 'Categoria del menu',
      type: 'select',
      optionSource: 'menuCategories',
    },
    {
      name: 'menuItemId',
      label: 'Producto del menu',
      type: 'select',
      optionSource: 'menuItems',
    },
  ],
  RESTAURANT_RAW_MATERIAL_USAGE: [
    ...dateRange,
    {
      name: 'rawMaterialCategoryId',
      label: 'Categoria de materia prima',
      type: 'select',
      optionSource: 'rawMaterialCategories',
    },
    {
      name: 'rawMaterialId',
      label: 'Materia prima',
      type: 'select',
      optionSource: 'rawMaterials',
    },
  ],
  RESTAURANT_RAW_MATERIAL_STOCK: [
    {
      name: 'stockStatus',
      label: 'Estado de stock',
      type: 'select',
      optionSource: 'stockStatuses',
    },
    {
      name: 'rawMaterialCategoryId',
      label: 'Categoria de materia prima',
      type: 'select',
      optionSource: 'rawMaterialCategories',
    },
    {
      name: 'rawMaterialId',
      label: 'Materia prima',
      type: 'select',
      optionSource: 'rawMaterials',
    },
  ],
  RESTAURANT_INVENTORY_MOVEMENTS: [
    ...dateRange,
    {
      name: 'movementType',
      label: 'Tipo de movimiento',
      type: 'select',
      optionSource: 'movementTypes',
    },
    {
      name: 'rawMaterialId',
      label: 'Materia prima',
      type: 'select',
      optionSource: 'rawMaterials',
    },
  ],
  RESTAURANT_PURCHASES: [
    ...dateRange,
    {
      name: 'supplierId',
      label: 'Proveedor',
      type: 'select',
      optionSource: 'suppliers',
    },
    {
      name: 'status',
      label: 'Estado',
      type: 'select',
      optionSource: 'purchaseStatuses',
    },
  ],
  RESTAURANT_RECIPE_COSTS: [
    {
      name: 'menuCategoryId',
      label: 'Categoria del menu',
      type: 'select',
      optionSource: 'menuCategories',
    },
    {
      name: 'menuItemId',
      label: 'Producto del menu',
      type: 'select',
      optionSource: 'menuItems',
    },
  ],
  RESTAURANT_KITCHEN_PERFORMANCE: [
    ...dateRange,
    {
      name: 'preparationStationId',
      label: 'Estacion',
      type: 'select',
      optionSource: 'preparationStations',
    },
  ],
  RESTAURANT_RESERVATIONS: [
    ...dateRange,
    {
      name: 'status',
      label: 'Estado',
      type: 'select',
      optionSource: 'reservationStatuses',
    },
    {
      name: 'diningRoomId',
      label: 'Salon',
      type: 'select',
      optionSource: 'diningRooms',
    },
  ],
  RESTAURANT_CORPORATE_CONSUMPTIONS: [
    ...dateRange,
    {
      name: 'accountId',
      label: 'Cuenta corporativa',
      type: 'select',
      optionSource: 'corporateAccounts',
    },
    {
      name: 'status',
      label: 'Estado',
      type: 'select',
      optionSource: 'corporateConsumptionStatuses',
    },
    {
      name: 'menuItemId',
      label: 'Platillo del menú',
      type: 'select',
      optionSource: 'menuItems',
    },
  ],
  RESTAURANT_WAITER_TIPS: [
    ...dateRange,
    {
      name: 'waiterId',
      label: 'Mesero',
      type: 'select',
      optionSource: 'waiters',
    },
  ],
  RESTAURANT_WAITER_TIPS_DETAIL: [
    ...dateRange,
    {
      name: 'waiterId',
      label: 'Mesero',
      type: 'select',
      optionSource: 'waiters',
    },
  ],
};

export const restaurantStaticReportOptions = {
  orderStatuses: [
    { id: 'OPEN', name: 'Abierta' },
    { id: 'CONFIRMED', name: 'Confirmada' },
    { id: 'IN_PREPARATION', name: 'En preparacion' },
    { id: 'READY', name: 'Lista' },
    { id: 'CLOSED', name: 'Cerrada' },
    { id: 'CANCELLED', name: 'Cancelada' },
  ],
  purchaseStatuses: [
    { id: 'DRAFT', name: 'Borrador' },
    { id: 'CONFIRMED', name: 'Confirmada' },
    { id: 'CANCELLED', name: 'Cancelada' },
  ],
  movementTypes: [
    { id: 'PURCHASE', name: 'Compra' },
    { id: 'CONSUMPTION', name: 'Consumo' },
    { id: 'ADJUSTMENT', name: 'Ajuste' },
    { id: 'PRODUCTION', name: 'Produccion' },
    { id: 'WASTE', name: 'Merma' },
  ],
  reservationStatuses: [
    { id: 'PENDING', name: 'Pendiente' },
    { id: 'CONFIRMED', name: 'Confirmada' },
    { id: 'SEATED', name: 'Sentada' },
    { id: 'CANCELLED', name: 'Cancelada' },
    { id: 'NO_SHOW', name: 'No asistio' },
  ],
  stockStatuses: [
    { id: 'LOW_STOCK', name: 'Bajo minimo' },
    { id: 'OUT_OF_STOCK', name: 'Agotado' },
    { id: 'OK', name: 'Disponible' },
  ],
  serviceTypes: [
    { id: 'DINE_IN', name: 'Mesa' },
    { id: 'TAKEAWAY', name: 'Para llevar' },
    { id: 'DELIVERY', name: 'Domicilio' },
  ],
  corporateConsumptionStatuses: [
    { id: 'DRAFT', name: 'Borrador' },
    { id: 'CONFIRMED', name: 'Confirmado' },
    { id: 'INVOICED', name: 'Facturado' },
    { id: 'CANCELLED', name: 'Cancelado' },
  ],
};
